import { z } from 'zod';
import { PRICE_OP_TYPES, ROUNDING_MODES } from '../money.js';
export const PRICE_TARGET_TYPES = ['ITEM', 'VARIANT'];
/** Pasos de redondeo que ofrece el dashboard (centavos). */
export const PRICE_ROUNDING_STEPS = [1000, 5000, 10000, 50000, 100000];
const idList = z.array(z.string().trim().min(1).max(64)).max(500);
export const priceScopeSchema = z
    .object({
    all: z.boolean().default(false),
    categoryIds: idList.default([]),
    itemIds: idList.default([]),
    includeVariants: z.boolean().default(true),
})
    .refine((s) => s.all || s.categoryIds.length > 0 || s.itemIds.length > 0, {
    message: 'Elegí todo el menú, alguna categoría o algún producto',
    path: ['categoryIds'],
});
export const priceOpSchema = z
    .object({
    type: z.enum(PRICE_OP_TYPES),
    value: z.number(),
})
    .superRefine((op, ctx) => {
    if (op.type === 'PERCENT') {
        if (op.value < -100 || op.value > 1000) {
            ctx.addIssue({ code: 'custom', path: ['value'], message: 'El porcentaje tiene que estar entre -100 y 1000' });
        }
        return;
    }
    if (!Number.isInteger(op.value)) {
        ctx.addIssue({ code: 'custom', path: ['value'], message: 'El monto tiene que estar en centavos enteros' });
    }
    else if (Math.abs(op.value) > 1_000_000_000) {
        ctx.addIssue({ code: 'custom', path: ['value'], message: 'Monto demasiado grande' });
    }
});
export const priceRoundingSchema = z
    .object({
    mode: z.enum(ROUNDING_MODES).default('NONE'),
    stepCents: z.int().min(1).max(10_000_000).optional(),
})
    .refine((r) => r.mode === 'NONE' || r.stepCents !== undefined, {
    message: 'Elegí el paso de redondeo',
    path: ['stepCents'],
});
export const priceExpectedChangeSchema = z.object({
    targetType: z.enum(PRICE_TARGET_TYPES),
    id: z.string().min(1).max(64),
    oldPriceCents: z.int(),
    newPriceCents: z.int(),
});
export const bulkPriceSchema = z.object({
    scope: priceScopeSchema,
    op: priceOpSchema,
    rounding: priceRoundingSchema.default({ mode: 'NONE' }),
    note: z.string().trim().max(200).optional(),
    /**
     * Solo en bulk-apply: los cambios que mostró el preview. Si el cálculo actual no coincide
     * (alguien cambió un precio en el medio) la API responde 409 PRICE_CHANGED.
     */
    expected: z.array(priceExpectedChangeSchema).max(5000).optional(),
});
// ---- Historial -----------------------------------------------------------------
const dateParam = z
    .string()
    .trim()
    .refine((s) => /^\d{4}-\d{2}-\d{2}$/.test(s) || !Number.isNaN(Date.parse(s)), 'Fecha inválida');
export const priceHistoryQuerySchema = z.object({
    itemId: z.string().trim().min(1).max(64).optional(),
    variantId: z.string().trim().min(1).max(64).optional(),
    batchId: z.string().trim().min(1).max(64).optional(),
    /** "YYYY-MM-DD" (día en la zona horaria del local) o ISO datetime. `to` es inclusivo. */
    from: dateParam.optional(),
    to: dateParam.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export const priceBatchesQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export const PRICE_CHANGE_REASON_LABELS = {
    MANUAL: 'Manual',
    BULK: 'Masivo',
    REVERT: 'Reversión',
};
export const REVERT_NOTE_PREFIX = 'Revierte ';
//# sourceMappingURL=prices.js.map